import { createDefaultMapDetail } from '../utils/map-embed.util';
import { DEFAULT_FORM_SECTION_BACKGROUND_IMAGE } from '../constants/form-config.constants';

export interface SectionVariant {
  id: string;
  name: string;
  description: string;
  previewImage?: string;
  isDefault?: boolean;
  config: any;
}

export interface SectionVariantConfig {
  sectionType: string;
  currentVariantId: string;
  variants: SectionVariant[];
}

export interface VariantSelectionData {
  sectionType: string;
  currentVariantId: string;
  variants: SectionVariant[];
  designSystemColors?: any;
  formBackgroundColor?: string;
  onVariantSelect: (variantId: string) => void;
}

// Predefined variants for each section type
export const SECTION_VARIANTS: Record<string, SectionVariant[]> = {
  aboutCompanySection: [
    {
      id: 'gallery-text',
      name: 'Gallery(Above) + Text(Below)',
      description: 'Display image gallery after that company information',
      isDefault: true,
      config: {
        showGallery: true,
        layout: 'gallery-text',
      },
    },
    {
      id: 'text-gallery',
      name: 'Text(Above) + Gallery(Below)',
      description: 'Display company information with image gallery',
      isDefault: true,
      config: {
        showGallery: true,
        layout: 'text-gallery',
      },
    },
    {
      id: 'text-only',
      name: 'Text Only',
      description: 'Display company information without gallery',
      config: {
        showGallery: false,
        layout: 'text-only',
      },
    },
  ],
  simpleBenefitsSection: [
    {
      id: 'text-only',
      name: 'Section title',
      description: 'Show the benefits list',
      config: {
        showButton: false,
      },
    },
    {
      id: 'text-cta',
      name: 'Section title + CTA button',
      description: 'Show CTA button below the benefits list',
      config: {
        showButton: true,
      },
    },
  ],
  benefitsWithIconsSection: [
    {
      id: 'text-cta',
      name: 'Section title + CTA button',
      description: 'Show CTA button below the benefits grid',
      config: {
        showButton: true,
      },
    },
    {
      id: 'text-only',
      name: 'Section title',
      description: 'Show the benefits grid',
      config: {
        showButton: false,
      },
    },
  ],
  heroSection: [
    {
      id: 'banner-left',
      name: 'Banner Image - Left Layout',
      description:
        'Banner image background with left-aligned content (title, button, location)',
      isDefault: true,
      config: {
        layout: 'banner-left',
        backgroundType: 'banner',
        contentAlignment: 'left',
        showContactForm: false,
        formPosition: null,
        backgroundColor: null,
      },
    },
    {
      id: 'solid-left',
      name: 'Solid Color - Left Layout',
      description:
        'Solid color background with left-aligned content (title, button, location)',
      config: {
        layout: 'solid-left',
        backgroundType: 'solid',
        contentAlignment: 'left',
        showContactForm: false,
        formPosition: null,
        backgroundColor: null,
      },
    },
    {
      id: 'banner-split',
      name: 'Banner Image - Split Layout (Form Right)',
      description:
        'Banner image background with split layout (left: content, right: contact form)',
      config: {
        layout: 'banner-split',
        backgroundType: 'banner',
        contentAlignment: 'split',
        showContactForm: true,
        formPosition: 'right',
        backgroundColor: null,
      },
    },
    {
      id: 'banner-split-left-form',
      name: 'Banner Image - Split Layout (Form Left)',
      description:
        'Banner image background with split layout (left: contact form, right: content)',
      config: {
        layout: 'banner-split',
        backgroundType: 'banner',
        contentAlignment: 'split',
        showContactForm: true,
        formPosition: 'left',
        backgroundColor: null,
      },
    },
    {
      id: 'solid-split',
      name: 'Solid Color - Split Layout (Form Right)',
      description:
        'Solid color background with split layout (left: content, right: contact form)',
      config: {
        layout: 'solid-split',
        backgroundType: 'solid',
        contentAlignment: 'split',
        showContactForm: true,
        formPosition: 'right',
        backgroundColor: null,
      },
    },
    {
      id: 'solid-split-left-form',
      name: 'Solid Color - Split Layout (Form Left)',
      description:
        'Solid color background with split layout (left: contact form, right: content)',
      config: {
        layout: 'solid-split',
        backgroundType: 'solid',
        contentAlignment: 'split',
        showContactForm: true,
        formPosition: 'left',
        backgroundColor: null,
      },
    },
  ],
  featuresSection: [
    {
      id: 'two-column',
      name: 'Two Column',
      description: 'Display features in two columns side by side',
      isDefault: true,
      config: {
        layout: 'two-column',
        columns: 2,
      },
    },
    {
      id: 'single-column',
      name: 'Single Column',
      description: 'Display features in a single column layout',
      config: {
        layout: 'single-column',
        columns: 1,
      },
    },
  ],
  jobDescriptionSection: [
    {
      id: 'text-image-right',
      name: '(Small) Text + Image (Right)',
      description: 'Display content with text and image (image on the right)',
      isDefault: true,
      config: {
        layout: 'text-image-right',
        imagePosition: 'right',
        height: '300px',
      },
    },
    {
      id: 'medium-text-image-right',
      name: '(Medium) Text + Image (Right)',
      description: 'Display content with text and image (image on the right)',
      isDefault: false,
      config: {
        layout: 'medium-text-image-right',
        imagePosition: 'right',
        height: '500px',
      },
    },
    {
      id: 'large-text-image-right',
      name: '(Large) Text + Image (Right)',
      description: 'Display content with text and image (image on the right)',
      isDefault: false,
      config: {
        layout: 'large-text-image-right',
        imagePosition: 'right',
        height: '700px',
      },
    },
    {
      id: 'small-text-image-left',
      name: '(Small) Text + Image (Left)',
      description: 'Display content with text and image (image on the left)',
      config: {
        layout: 'small-text-image-left',
        imagePosition: 'left',
        height: '300px',
      },
    },
    {
      id: 'medium-text-image-left',
      name: '(Medium) Text + Image (Left)',
      description: 'Display content with text and image (image on the left)',
      config: {
        layout: 'medium-text-image-left',
        imagePosition: 'left',
        height: '500px',
      },
    },
    {
      id: 'large-text-image-left',
      name: '(Large) Text + Image (Left)',
      description: 'Display content with text and image (image on the left)',
      config: {
        layout: 'large-text-image-left',
        imagePosition: 'left',
        height: '700px',
      },
    },
    {
      id: 'text-only',
      name: 'Text Only',
      description: 'Display content as text only without image',
      config: {
        layout: 'text-only',
      },
    },
  ],
  applicationProcessSection: [
    {
      id: 'steps-recruiter',
      name: 'Application steps vertical (left) + Recruiter (right)',
      description: 'Display application steps with recruiter information',
      isDefault: true,
      config: {
        layout: 'steps-recruiter',
        showRecruiterInfo: true,
        showLocationMap: false,
      },
    },
    {
      id: 'recruiter-steps',
      name: 'Recruiter (left) + Application steps vertical (right)',
      description: 'Display recruiter information after that application steps',
      isDefault: true,
      config: {
        layout: 'recruiter-steps',
        showRecruiterInfo: true,
        showLocationMap: false,
      },
    },
    {
      id: 'vertical-steps-only',
      name: 'Application steps vertical only',
      description: 'Display application steps without recruiter information',
      config: {
        layout: 'vertical-steps-only',
        showRecruiterInfo: false,
        showLocationMap: false,
      },
    },
    {
      id: 'horizontal-steps-only',
      name: 'Application steps horizontal only',
      description: 'Display application steps without recruiter information',
      config: {
        layout: 'horizontal-steps-only',
        showRecruiterInfo: false,
        showLocationMap: false,
      },
    },
    {
      id: 'steps-left-map-right',
      name: 'Application process (left) + Map (right)',
      description: 'Display application process (left) and map (right)',
      config: {
        layout: 'steps-left-map-right',
        showRecruiterInfo: false,
        showLocationMap: true,
        mapDetail: createDefaultMapDetail(),
      },
    },
    {
      id: 'map-left-steps-right',
      name: 'Map (left) + Application process (right)',
      description: 'Display Map (right) and application process (left)',
      config: {
        layout: 'map-left-steps-right',
        showRecruiterInfo: false,
        showLocationMap: true,
        mapDetail: createDefaultMapDetail(),
      },
    },
  ],
  contactUsSection: [
    {
      id: 'standard-form',
      name: 'Contact Form + Solid Background',
      description: 'Form with a configurable solid background color',
      isDefault: true,
      config: {
        layout: 'standard-form',
        formAlignment: 'center',
        backgroundType: 'solid',
        backgroundColor: '#4d148c',
      },
    },
    {
      id: 'centered-form-solid-bg',
      name: 'Centered Form + Solid Background',
      description: 'Form centered with a configurable solid background color',
      config: {
        layout: 'centered-form-solid-bg',
        formAlignment: 'center',
        backgroundType: 'solid',
        backgroundColor: '#4d148c',
      },
    },
    {
      id: 'centered-form-bg-image',
      name: 'Centered Form + Background Image',
      description: 'Form centered with a background image behind the section',
      config: {
        layout: 'centered-form-bg-image',
        formAlignment: 'center',
        backgroundType: 'image',
        backgroundImageUrl: DEFAULT_FORM_SECTION_BACKGROUND_IMAGE,
      },
    },
    {
      id: 'left-form-bg-image',
      name: 'Left Form + Background Image',
      description: 'Form aligned to the left with a background image',
      config: {
        layout: 'left-form-bg-image',
        formAlignment: 'left',
        backgroundType: 'image',
        backgroundImageUrl: DEFAULT_FORM_SECTION_BACKGROUND_IMAGE,
      },
    },
    {
      id: 'right-form-bg-image',
      name: 'Right Form + Background Image',
      description: 'Form aligned to the right with a background image',
      config: {
        layout: 'right-form-bg-image',
        formAlignment: 'right',
        backgroundType: 'image',
        backgroundImageUrl: DEFAULT_FORM_SECTION_BACKGROUND_IMAGE,
      },
    },
  ],
  job_location_map: [
    {
      id: 'full-width-map',
      name: 'Large map (full page width)',
      description: 'Display map in full width without split layout',
      isDefault: true,
      config: { layout: 'full-width-map' },
    },
    {
      id: 'full-width-map-centered',
      name: 'Large map (centered)',
      description:
        'Display map in full width in the center without split layout',
      isDefault: true,
      config: { layout: 'full-width-map-centered' },
    },
    {
      id: 'map-left-paragraph-right',
      name: 'Map (left) + Paragraph (right)',
      description: 'Split screen: map on the left, paragraph on the right',
      isDefault: false,
      config: { layout: 'map-left-paragraph-right' },
    },
    {
      id: 'paragraph-left-map-right',
      name: 'Paragraph (left) + Map (right)',
      description: 'Split screen: paragraph on the left, map on the right',
      config: { layout: 'paragraph-left-map-right' },
    },
    {
      id: 'map-left-steps-right',
      name: 'Map (left) + Steps (right)',
      description: 'Split screen: map on the left, steps on the right',
      config: { layout: 'map-left-steps-right' },
    },
    {
      id: 'steps-left-map-right',
      name: 'Steps (left) + Map (right)',
      description: 'Split screen: steps on the left, map on the right',
      config: { layout: 'steps-left-map-right' },
    },
    {
      id: 'map-left-recruiter-right',
      name: 'Map (left) + Recruiter (right)',
      description: 'Split screen: map on the left, recruiter on the right',
      config: { layout: 'map-left-recruiter-right' },
    },
    {
      id: 'recruiter-left-map-right',
      name: 'Recruiter (left) + Map (right)',
      description: 'Split screen: recruiter on the left, map on the right',
      config: { layout: 'recruiter-left-map-right' },
    },
  ],
  footerSection: [
    {
      id: 'details-logo',
      name: 'Details(Left) + Logo(Right)',
      description: 'Complete footer with all contact information and logo',
      isDefault: true,
      config: {
        layout: 'details-logo',
        showContactInfo: true,
        showLogo: true,
      },
    },
    {
      id: 'logo-details',
      name: 'Logo(Left) + Details(Right)',
      description: 'Complete footer with logo and all contact information',
      isDefault: true,
      config: {
        layout: 'logo-details',
        showContactInfo: true,
        showLogo: true,
      },
    },
    {
      id: 'minimal-footer',
      name: 'Minimal Footer',
      description: 'Minimal footer with basic information only',
      config: {
        layout: 'minimal-footer',
        showContactInfo: false,
        showLogo: false,
      },
    },
  ],
  paragraphSection: [
    {
      id: 'text-only',
      name: 'Section text',
      description: 'Display section text only',
      isDefault: true,
      config: {
        layout: 'text-only',
        showTitle: false,
        showCtaButton: false,
      },
    },
    {
      id: 'text-only-left',
      name: 'Section text half width left aligned',
      description: 'Display section text left aligned',
      isDefault: true,
      config: {
        layout: 'text-only-left',
        showTitle: false,
        showCtaButton: false,
      },
    },
    {
      id: 'text-only-centered',
      name: 'Section text centered',
      description: 'Display section text centered',
      isDefault: true,
      config: {
        layout: 'text-only-centered',
        showTitle: false,
        showCtaButton: false,
      },
    },
    {
      id: 'text-only-right',
      name: 'Section text half width right aligned',
      description: 'Display section text right aligned',
      isDefault: true,
      config: {
        layout: 'text-only-right',
        showTitle: false,
        showCtaButton: false,
      },
    },
    {
      id: 'title-only',
      name: 'Section title',
      description: 'Display section title only',
      config: {
        layout: 'title-only',
        showTitle: true,
        showCtaButton: false,
      },
    },
    {
      id: 'title-only-left',
      name: 'Section title left aligned',
      description: 'Display section title left aligned',
      config: {
        layout: 'title-only-left',
        showTitle: true,
        showCtaButton: false,
      },
    },
    {
      id: 'title-only-centered',
      name: 'Section title centered',
      description: 'Display section title centered',
      config: {
        layout: 'title-only-centered',
        showTitle: true,
        showCtaButton: false,
      },
    },
    {
      id: 'title-only-right',
      name: 'Section title right aligned',
      description: 'Display section title right aligned',
      config: {
        layout: 'title-only-right',
        showTitle: true,
        showCtaButton: false,
      },
    },
    {
      id: 'title-text',
      name: 'Section title + Section text',
      description: 'Display section title and section text',
      config: {
        layout: 'title-text',
        showTitle: true,
        showCtaButton: false,
      },
    },
    {
      id: 'title-text-left',
      name: 'Section title + Section text left aligned',
      description: 'Display section title and section text left aligned',
      config: {
        layout: 'title-text-left',
        showTitle: true,
        showCtaButton: false,
      },
    },
    {
      id: 'title-text-centered',
      name: 'Section title + Section text centered',
      description: 'Display section title and section text centered',
      config: {
        layout: 'title-text-centered',
        showTitle: true,
        showCtaButton: false,
      },
    },
    {
      id: 'title-text-right',
      name: 'Section title + Section text right aligned',
      description: 'Display section title and section text right aligned',
      config: {
        layout: 'title-text-right',
        showTitle: true,
        showCtaButton: false,
      },
    },
    {
      id: 'title-cta',
      name: 'Section title  + CTA button',
      description: 'Display section title, and CTA button',
      config: {
        layout: 'title-cta',
        showTitle: true,
        showCtaButton: true,
      },
    },
    {
      id: 'title-cta-left',
      name: 'Section title  + CTA button left aligned',
      description: 'Display section title, and CTA button left aligned',
      config: {
        layout: 'title-cta-left',
        showTitle: true,
        showCtaButton: true,
      },
    },
    {
      id: 'title-cta-centered',
      name: 'Section title  + CTA button centered',
      description: 'Display section title, and CTA button centered',
      config: {
        layout: 'title-cta-centered',
        showTitle: true,
        showCtaButton: true,
      },
    },
    {
      id: 'title-cta-right',
      name: 'Section title  + CTA button right aligned',
      description: 'Display section title, and CTA button right aligned',
      config: {
        layout: 'title-cta-right',
        showTitle: true,
        showCtaButton: true,
      },
    },
    {
      id: 'title-text-cta',
      name: 'Section title + Section text + CTA button',
      description: 'Display section title, section text, and CTA button',
      config: {
        layout: 'title-text-cta',
        showTitle: true,
        showCtaButton: true,
      },
    },
    {
      id: 'title-text-cta-left',
      name: 'Section title + Section text + CTA button left aligned',
      description:
        'Display section title, section text, and CTA button left aligned',
      config: {
        layout: 'title-text-cta-left',
        showTitle: true,
        showCtaButton: true,
      },
    },
    {
      id: 'title-text-cta-centered',
      name: 'Section title + Section text + CTA button centered',
      description:
        'Display section title, section text, and CTA button centered',
      config: {
        layout: 'title-text-cta-centered',
        showTitle: true,
        showCtaButton: true,
      },
    },
    {
      id: 'title-text-cta-right',
      name: 'Section title + Section text + CTA button right aligned',
      description:
        'Display section title, section text, and CTA button right aligned',
      config: {
        layout: 'title-text-cta-right',
        showTitle: true,
        showCtaButton: true,
      },
    },
  ],
  recruiterSection: [
    {
      id: 'image-left',
      name: 'Recruiter full page (image left + text right)',
      description:
        'Show recruiter photo on the left and recruiter information on the right',
      isDefault: true,
      config: {
        layout: 'image-left',
      },
    },
    {
      id: 'image-right',
      name: 'Recruiter full page (text left + image right)',
      description:
        'Show recruiter information on the left and recruiter photo on the right',
      config: {
        layout: 'image-right',
      },
    },
    {
      id: 'text-card-right',
      name: 'Content (left) + Recruiter card small (right)',
      description:
        'Show recruiter photo on the left with recruiter info in a card on the right',
      config: {
        layout: 'text-card-right',
      },
    },
    {
      id: 'text-card-left',
      name: 'Recruiter card small (left) + Content (right)',
      description:
        'Show recruiter info in a card on the left with recruiter photo on the right',
      config: {
        layout: 'text-card-left',
      },
    },
    {
      id: 'dark-card-left',
      name: 'Recruiter card large (left)',
      description: 'Show recruiter info in a card on the left in dark mode',
      config: {
        layout: 'dark-card-left',
      },
    },
    {
      id: 'dark-card-left-text-right',
      name: 'Recruiter card large (left) + Content & CTA (right)',
      description:
        'Show recruiter info in a card on the left in dark mode with content on right',
      config: {
        layout: 'dark-card-left-text-right',
        showCtaButton: true,
      },
    },
    {
      id: 'dark-card-left-map-right',
      name: 'Recruiter card large (left) + Map (right)',
      description:
        'Show recruiter info in a card on the left in dark mode with map on right',
      config: {
        layout: 'dark-card-left-map-right',
        mapDetail: createDefaultMapDetail(),
      },
    },
  ],
  testimonialSection: [
    {
      id: 'testimonial-only',
      name: 'Testimonial Only',
      description: 'Show a testimonial with image, message and name',
      isDefault: true,
      config: {
        layout: 'testimonial-only',
      },
    },
    {
      id: 'testimonial-testimonial',
      name: 'Testimonial (Left) + Testimonial (Right)',
      description:
        'Show testimonial + testimonial with image, message and name',
      isDefault: false,
      config: {
        layout: 'testimonial-testimonial',
      },
    },
    {
      id: 'testimonial-paragraph',
      name: 'Testimonial (Left) + Paragraph (Right)',
      description: 'Show testimonial with image, message and name + paragraph',
      isDefault: false,
      config: {
        layout: 'testimonial-paragraph',
      },
    },
    {
      id: 'paragraph-testimonial',
      name: 'Paragraph (Left) + Testimonial (Right)',
      description: 'Show testimonial with image, message and name + paragraph',
      isDefault: false,
      config: {
        layout: 'paragraph-testimonial',
      },
    },
  ],
  amChartSection: [
    {
      id: 'diagram-left',
      name: 'Diagram (left)',
      description: 'Diagram aligned on the left',
      isDefault: true,
      config: { layout: 'diagram-left' },
    },
    {
      id: 'title-diagram-left',
      name: 'Diagram + title (left)',
      description: 'Diagram aligned on the left with the title',
      isDefault: true,
      config: { layout: 'title-diagram-left' },
    },
    {
      id: 'diagram-center',
      name: 'Diagram (center)',
      description: 'Diagram centered',
      config: { layout: 'diagram-center' },
    },
    {
      id: 'title-diagram-center',
      name: 'Diagram + title (center)',
      description: 'Diagram aligned on the centered with the title',
      config: { layout: 'title-diagram-center' },
    },
    {
      id: 'diagram-right',
      name: 'Diagram (right)',
      description: 'Diagram aligned on the right',
      config: { layout: 'diagram-right' },
    },
    {
      id: 'title-diagram-right',
      name: 'Diagram  + title (right)',
      description: 'Diagram aligned on the right with the title',
      config: { layout: 'title-diagram-right' },
    },
    {
      id: 'diagram-left-content-right',
      name: 'Diagram (left) + Content (right)',
      description: 'Diagram on the left, content on the right',
      config: { layout: 'diagram-left-content-right' },
    },
    {
      id: 'title-diagram-left-content-right',
      name: 'Diagram + title (left) + Content (right)',
      description: 'Diagram on the left with the title, content on the right',
      config: { layout: 'title-diagram-left-content-right' },
    },
    {
      id: 'content-left-diagram-right',
      name: 'Content (left) + Diagram (right)',
      description: 'Content on the left, diagram on the right',
      config: { layout: 'content-left-diagram-right' },
    },
    {
      id: 'title-content-left-diagram-right',
      name: 'Content (left) + Diagram + title (right)',
      description: 'Content on the left, diagram on the right with the title',
      config: { layout: 'title-content-left-diagram-right' },
    },
    {
      id: 'diagram-left-media-right',
      name: 'Diagram (left) + Media (right)',
      description: 'Diagram on the left, media on the right',
      config: { layout: 'diagram-left-media-right' },
    },
    {
      id: 'media-left-diagram-right',
      name: 'Media (left) + Diagram (right)',
      description: 'Media on the left, diagram on the right',
      config: { layout: 'media-left-diagram-right' },
    },
    {
      id: 'content-center',
      name: 'Content (center)',
      description: 'Show centered content without the chart',
      config: { layout: 'content-center' },
    },
  ],
  horizontalListSection: [
    {
      id: 'title',
      name: 'Title',
      description: 'Horizontal list with title only per item',
      config: {
        showIcon: false,
        showDescription: false,
      },
    },
    {
      id: 'icon-title',
      name: 'Icon + Title',
      description: 'Horizontal list with icon and title per item',
      config: {
        showIcon: true,
        showDescription: false,
      },
    },
    {
      id: 'title-description',
      name: 'Title + Description',
      description: 'Horizontal list with title and description per item',
      config: {
        showIcon: false,
        showDescription: true,
      },
    },
    {
      id: 'icon-title-description',
      name: 'Icon + Title + Description',
      description: 'Horizontal list with icon, title and description per item',
      isDefault: true,
      config: {
        showIcon: true,
        showDescription: true,
      },
    },
  ],
  imagesGallerySection: [
    {
      id: 'gallery-carousel',
      name: 'Image Gallery (with title)',
      description: 'Display multiple office images in a gallery layout',
      isDefault: true,
      config: {
        layout: 'gallery',
        showTitle: true,
      },
    },
    {
      id: 'gallery-carousel-no-title',
      name: 'Image Gallery (without Title)',
      description: 'Display multiple office images without section title',
      config: {
        layout: 'gallery',
        showTitle: false,
      },
    },
    {
      id: 'single-image',
      name: 'Large image (centered)',
      description: 'Display one large highlighted office image in the center',
      config: {
        layout: 'single-image',
        showTitle: false,
      },
    },
    {
      id: 'single-large-image',
      name: 'Large image (full page width)',
      description: 'Display one large highlighted office image',
      config: {
        layout: 'single-large-image',
        showTitle: false,
      },
    },
  ],
  mediaSection: [
    {
      id: 'video-only',
      name: 'Video Only',
      description: 'Display a standalone video section',
      isDefault: true,
      config: {
        layout: 'video-only',
        showTitle: true,
      },
    },
    {
      id: 'video-paragraph',
      name: 'Video (left) + Paragraph (right)',
      description: 'Show video on left with text on right',
      isDefault: false,
      config: {
        layout: 'video-paragraph',
        showTitle: true,
      },
    },
    {
      id: 'paragraph-video',
      name: 'Paragraph (left) + video (right)',
      description: 'Show text on left with video on right',
      isDefault: false,
      config: {
        layout: 'paragraph-video',
        showTitle: true,
      },
    },
    {
      id: 'video-vertical-paragraph',
      name: 'Vertical Video (left) + Paragraph (right)',
      description: 'Show vertical video on left with text on right',
      isDefault: false,
      config: {
        layout: 'video-vertical-paragraph',
        showTitle: true,
      },
    },
    {
      id: 'paragraph-video-vertical',
      name: 'Paragraph (left) + Vertical Video (right) ',
      description:
        'Show paragraph text in the left and vertical video on right',
      isDefault: false,
      config: {
        layout: 'paragraph-video-vertical',
        showTitle: true,
      },
    },
    {
      id: 'video-process',
      name: 'Video (left) + Application process (right)',
      description: 'Show video with application process steps',
      isDefault: false,
      config: {
        layout: 'video-process',
        showTitle: true,
      },
    },
    {
      id: 'process-video',
      name: 'Application process (left) + Video (right)',
      description: 'Show process steps with video beside',
      isDefault: false,
      config: {
        layout: 'process-video',
        showTitle: true,
      },
    },
    {
      id: 'video-recruiter',
      name: 'Video (left) + Recruiter (right)',
      description: 'Show video with recruiter details',
      isDefault: false,
      config: {
        layout: 'video-recruiter',
        showTitle: true,
      },
    },
    {
      id: 'recruiter-video',
      name: 'Recruiter (left) + video (right)',
      description: 'Show recruiter details with video',
      isDefault: false,
      config: {
        layout: 'recruiter-video',
        showTitle: true,
      },
    },
    {
      id: 'video-map',
      name: 'Video (left) + Map (right)',
      description: 'Show video alongside location map',
      isDefault: false,
      config: {
        layout: 'video-map',
        showTitle: true,
        mapDetail: createDefaultMapDetail(),
      },
    },
    {
      id: 'map-video',
      name: 'Map (left) + Video (right)',
      description: 'Show map with video beside',
      isDefault: false,
      config: {
        layout: 'map-video',
        showTitle: true,
        mapDetail: createDefaultMapDetail(),
      },
    },
    {
      id: 'video-testimonial',
      name: 'Video (left) + Testimonial (right)',
      description: 'Show video with testimonial content',
      isDefault: false,
      config: {
        layout: 'video-testimonial',
        showTitle: true,
      },
    },
    {
      id: 'testimonial-video',
      name: 'Testimonial (left) + Video (right)',
      description: 'Show testimonial with video beside',
      isDefault: false,
      config: {
        layout: 'testimonial-video',
        showTitle: true,
      },
    },
  ],
  verticalListSection: [
    {
      id: 'vertical-list',
      name: 'Vertical list (left aligned)',
      description: 'Display a vertical list aligned to the left',
      isDefault: true,
      config: {
        layout: 'vertical-list',
      },
    },
    {
      id: 'vertical-list-paragraph',
      name: 'Vertical list (left) + Paragraph (right)',
      description: 'Vertical list on left and paragraph content on right',
      config: {
        layout: 'vertical-list-paragraph',
      },
    },
    {
      id: 'paragraph-vertical-list',
      name: 'Paragraph (left) + Vertical list (right)',
      description: 'Paragraph on left and vertical list on right',
      config: {
        layout: 'paragraph-vertical-list',
      },
    },
    {
      id: 'vertical-list-image',
      name: 'Vertical list (left) + Image (right)',
      description: 'Vertical list on left and image on right',
      config: {
        layout: 'vertical-list-image',
      },
    },
    {
      id: 'image-vertical-list',
      name: 'Image (left) + Vertical list (right)',
      description: 'Image on left and vertical list on right',
      config: {
        layout: 'image-vertical-list',
      },
    },
    {
      id: 'vertical-list-recruiter',
      name: 'Vertical list (left) + Recruiter (right)',
      description: 'Vertical list on left and recruiter details on right',
      config: {
        layout: 'vertical-list-recruiter',
      },
    },
    {
      id: 'recruiter-vertical-list',
      name: 'Recruiter (left) + Vertical list (right)',
      description: 'Recruiter details on left and vertical list on right',
      config: {
        layout: 'recruiter-vertical-list',
      },
    },
  ],
};
